var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { BaseTerrainProvider } from "./BaseTerrainProvider";
import { GeographicTilingScheme, WebMercatorTilingScheme } from "../../math/tiling";
import { Cartographic, Rectangle } from "../../math";
import { BufferGeometry, Float32BufferAttribute, Sphere, Uint16BufferAttribute, Uint32BufferAttribute, Vector3, } from "three";
import { FileLoader } from "../loaders";
/**
 * A terrain provider that will fetch 3D terrain tiles from some 'quantized-mesh' tile source.
 */
export class QuantizedMeshTerrainProvider extends BaseTerrainProvider {
    constructor(url, layer, options) {
        var _a;
        super();
        /**
         * A wrapping index that tracks the next index to use into the _partialUrls list for fetching tiles from
         * @private
         */
        this._urlIndex = 0;
        this._layer = layer;
        this._attributions = (_a = options === null || options === void 0 ? void 0 : options.attributions) !== null && _a !== void 0 ? _a : [];
        this._bearerToken = (options === null || options === void 0 ? void 0 : options.bearerToken) !== undefined ? `Bearer ${options.bearerToken}` : undefined;
        // Select the tiling scheme based on the layer's declared scheme. We only accept specific projection/scheme
        // pairs so this may throw errors.
        switch (this._layer.scheme) {
            case "tms":
                if (this._layer.projection === "EPSG:3857")
                    throw new Error("Unsupported scheme/projection pair (tms + EPSG:3857)");
                this._tilingScheme = new GeographicTilingScheme();
                break;
            case "slippyMap":
                if (this._layer.projection === "EPSG:4326")
                    throw new Error("Unsupported scheme/projection pair (slippyMap + EPSG:4326)");
                this._tilingScheme = new WebMercatorTilingScheme();
                break;
        }
        // Get the intersecting region of our requested 'region' and the tiling scheme's region. The provider can
        // only provide tiles from within the intersection of both regions.
        this._region = Rectangle.MAX;
        if (this._tilingScheme.rectangle.intersects(this._region)) {
            this._region = this._tilingScheme.rectangle.intersection(this._region);
        }
        else {
            throw new Error("The requested 'region' does not intersect with 'tilingScheme.rectangle'");
        }
        // Get the intersection of our constrained region and the declared bounds in the terrain layer.json
        //
        // Intersect with Rectangle.MAX to clamp to Rectangle.MAX in case of numerical precision issues with
        // 'fromDegrees'
        const terrainBounds = new Rectangle()
            .fromDegrees(this._layer.bounds[0], this._layer.bounds[1], this._layer.bounds[2], this._layer.bounds[3])
            .intersection(Rectangle.MAX);
        // Assert that the bounds are legal
        if (!terrainBounds.isLegal())
            throw new Error("Terrain layer.json bounds are not legal rectangle");
        // Get the intersection of our requested region (+ tiling scheme region) and the layer.json bounds. This should
        // give us a fully constrained region that encodes the region where all 3 areas are valid.
        if (terrainBounds.intersects(this._region)) {
            this._region = terrainBounds.intersection(this._region);
        }
        else {
            throw new Error("The layer bounds do not intersect with the requested 'region'");
        }
        // Check we have a supported layer and mesh format
        if (!this._layer.version.startsWith("1.")) {
            throw new Error(`Unsupported terrain layer.json version '${this._layer.version}'`);
        }
        if (this._layer.format !== "quantized-mesh-1.0") {
            throw new Error(`Unsupported quantized-mesh format version '${this._layer.format}'`);
        }
        // Provide a partial completion of the template url with the version pre resolved
        this._partialUrls = this._layer.tiles.map((v) => {
            const versioned = v.replace("{version}", this._layer.version);
            return `${url}${versioned}`;
        });
    }
    get tilingScheme() {
        return this._tilingScheme;
    }
    get region() {
        return this._region;
    }
    get attributions() {
        return this._attributions;
    }
    get minLevel() {
        return this._layer.minzoom;
    }
    get maxLevel() {
        return this._layer.maxzoom;
    }
    loadTile(x, y, zoomLevel) {
        return __awaiter(this, void 0, void 0, function* () {
            const headers = {
                Accept: "application/vnd.quantized-mesh,application/octet-stream;q=0.9",
            };
            if (this._bearerToken !== undefined) {
                headers.Authorization = this._bearerToken;
            }
            // Increment and wrap the _urlIndex value
            this._urlIndex = (((this._urlIndex | 0) + 1) | 0) % (this._layer.tiles.length | 0); // | 0 to hint integer ops
            // Flip y as the tms scheme counts from the bottom left
            let requestY;
            if (this._layer.scheme === "tms") {
                requestY = this._tilingScheme.getNumberOfYTilesAtLevel(zoomLevel) - y - 1;
            }
            else {
                requestY = y;
            }
            let url = this._partialUrls[this._urlIndex];
            url = url.replace("{z}", zoomLevel.toString());
            url = url.replace("{x}", x.toString());
            url = url.replace("{y}", requestY.toString());
            const data = yield FileLoader.loadArrayBuffer(url, {
                headers,
            });
            return [this.loadQuantizedMesh(x, y, zoomLevel, new DataView(data))];
        });
    }
    loadQuantizedMesh(x, y, zoomLevel, data) {
        // Read file header
        const center = this.readCenter(data);
        const heights = this.readHeightRange(data);
        const boundingSphere = this.readBoundingSphere(data, center);
        const _horizonOcclusionPoint = this.readHorizonOcclusionPoint(data);
        // Read vertex buffer data
        const [vertexCount, uOffset, vOffset, heightOffset] = this.calculateVertexDataOffsets(data);
        const uBuffer = this.readVertexDataChannel(data, uOffset, vertexCount);
        const vBuffer = this.readVertexDataChannel(data, vOffset, vertexCount);
        const heightBuffer = this.readVertexDataChannel(data, heightOffset, vertexCount);
        // Read index buffer data
        const areIndices32bit = vertexCount > 65536;
        const indexOffset = this.calculateIndexDataOffsets(heightOffset, vertexCount, areIndices32bit);
        const triangleIndices = this.readIndexBuffer(data, indexOffset, areIndices32bit);
        // Read edge index buffer data
        const westEdgeOffset = this.calculateEdgeIndexDataOffsets(indexOffset, triangleIndices.length, areIndices32bit);
        const westEdgeIndices = this.readEdgeIndexBuffer(data, westEdgeOffset, areIndices32bit);
        const southEdgeOffset = this.calculateEdgeIndexDataOffsets(westEdgeOffset, westEdgeIndices.length, areIndices32bit);
        const southEdgeIndices = this.readEdgeIndexBuffer(data, southEdgeOffset, areIndices32bit);
        const eastEdgeOffset = this.calculateEdgeIndexDataOffsets(southEdgeOffset, southEdgeIndices.length, areIndices32bit);
        const eastEdgeIndices = this.readEdgeIndexBuffer(data, eastEdgeOffset, areIndices32bit);
        const northEdgeOffset = this.calculateEdgeIndexDataOffsets(eastEdgeOffset, eastEdgeIndices.length, areIndices32bit);
        const northEdgeIndices = this.readEdgeIndexBuffer(data, northEdgeOffset, areIndices32bit);
        // Compute vertex positions
        const [positions, uvs] = this.decodeQuantizedVertexData(x, y, zoomLevel, heights, uBuffer, vBuffer, heightBuffer, center);
        // Generate the skirt to fill the gap between tiles of different LODs
        const skirtGeometry = this.calculateSkirtGeometry(positions, uvs, center, westEdgeIndices, southEdgeIndices, eastEdgeIndices, northEdgeIndices);
        const indexAttribute = areIndices32bit
            ? new Uint32BufferAttribute(triangleIndices, 1, false)
            : new Uint16BufferAttribute(triangleIndices, 1, false);
        const geometry = new BufferGeometry();
        geometry.boundingSphere = boundingSphere;
        geometry.setAttribute("position", new Float32BufferAttribute(positions, 3, false));
        geometry.setAttribute("uv", new Float32BufferAttribute(uvs, 2, false));
        geometry.setIndex(indexAttribute);
        return { geometry, skirtGeometry, midpoint: center };
    }
    /**
     * Reads the tile center from the terrain tile data
     * @param data
     * @private
     */
    readCenter(data) {
        const center = new Vector3();
        center.x = data.getFloat64(HEADER_OFFSET + 0, true);
        center.y = data.getFloat64(HEADER_OFFSET + 8, true);
        center.z = data.getFloat64(HEADER_OFFSET + 16, true);
        return center;
    }
    /**
     * Reads the tile height min/max from the terrain tile data
     * @param data
     * @private
     */
    readHeightRange(data) {
        const minimumHeight = data.getFloat32(HEADER_OFFSET + 24, true);
        const maximumHeight = data.getFloat32(HEADER_OFFSET + 28, true);
        return [minimumHeight, maximumHeight];
    }
    /**
     * Reads the bounding sphere information from the terrain tile data
     * @param data
     * @param center
     * @private
     */
    readBoundingSphere(data, center) {
        const boundingSphere = new Sphere();
        boundingSphere.center.x = data.getFloat64(HEADER_OFFSET + 32, true);
        boundingSphere.center.y = data.getFloat64(HEADER_OFFSET + 40, true);
        boundingSphere.center.z = data.getFloat64(HEADER_OFFSET + 48, true);
        boundingSphere.radius = data.getFloat64(HEADER_OFFSET + 56, true);
        boundingSphere.center.sub(center);
        return boundingSphere;
    }
    /**
     * Reads the horizon occlusion point from the terrain tile data
     * @param data
     * @private
     */
    readHorizonOcclusionPoint(data) {
        const point = new Vector3();
        point.x = data.getFloat64(HEADER_OFFSET + 64, true);
        point.y = data.getFloat64(HEADER_OFFSET + 72, true);
        point.z = data.getFloat64(HEADER_OFFSET + 80, true);
        return point;
    }
    /**
     * Calculates the vertex count and offsets for each of the vertex data channels
     * @param data
     * @private
     */
    calculateVertexDataOffsets(data) {
        const vertexCount = data.getUint32(VTX_DATA_OFFSET + 0, true);
        const uOffset = VTX_DATA_OFFSET + 4;
        const vOffset = uOffset + 2 * vertexCount;
        const heightOffset = vOffset + 2 * vertexCount;
        return [vertexCount, uOffset, vOffset, heightOffset];
    }
    /**
     * Handles reading a single vertex data channel, with the given offset and length.
     *
     * Data is expected to be 'zig zag' encoded. This function will decode the encoded values.
     * @param data
     * @param offset
     * @param count
     * @private
     */
    readVertexDataChannel(data, offset, count) {
        let acc = 0;
        return Array.from({ length: count }, (_, k) => {
            const encoded = data.getUint16(offset + 2 * k, true);
            acc += zigZagDecode(encoded);
            return acc;
        });
    }
    /**
     * Handles calculating the index data offset.
     *
     * The index data is immediately after the 'height' data, forward aligned to 4 byte alignment.
     * @param heightOffset
     * @param vertexCount
     * @private
     */
    calculateIndexDataOffsets(heightOffset, vertexCount, areIndices32bit) {
        const neededAlignment = areIndices32bit ? 4 : 2;
        const unalignedIndexOffset = heightOffset + 2 * vertexCount;
        const bytesToPreviousAlignedOffset = (unalignedIndexOffset | 0) % neededAlignment;
        if (bytesToPreviousAlignedOffset === 0) {
            return unalignedIndexOffset;
        }
        else {
            const bytesToNextAlignedOffset = neededAlignment - bytesToPreviousAlignedOffset;
            return unalignedIndexOffset + bytesToNextAlignedOffset;
        }
    }
    /**
     * Read and decode the index buffer
     *
     * @param data
     * @param offset
     * @param areIndices32bit
     * @private
     */
    readIndexBuffer(data, offset, areIndices32bit) {
        // If we have more than 65536 vertices then the file encodes 32-bit indices. We switch our 'fetcher' based on
        // this to transparently load either 16 bit or 32 bit indices in the index decoder
        let indexFetcher;
        if (areIndices32bit) {
            indexFetcher = (i) => data.getUint32(offset + 4 + 4 * i, true);
        }
        else {
            indexFetcher = (i) => data.getUint16(offset + 4 + 2 * i, true);
        }
        // Data is zigzag encoded
        let highest = 0;
        const triangleCount = data.getUint32(offset, true);
        return Array.from({ length: triangleCount * 3 }, (_, k) => {
            const encoded = indexFetcher(k);
            const decoded = highest - encoded;
            if (encoded === 0) {
                highest += 1;
            }
            return decoded;
        });
    }
    /**
     * Calculate the offsets to the edge indices
     *
     * @param indexOffset
     * @param indexCount
     * @returns
     */
    calculateEdgeIndexDataOffsets(indexOffset, indexCount, areIndices32bit) {
        const indexSize = areIndices32bit ? 4 : 2;
        const neededAlignment = indexSize;
        const unalignedIndexOffset = indexOffset + 4 + indexSize * indexCount;
        const bytesToPreviousAlignedOffset = (unalignedIndexOffset | 0) % neededAlignment;
        if (bytesToPreviousAlignedOffset === 0) {
            return unalignedIndexOffset;
        }
        else {
            const bytesToNextAlignedOffset = neededAlignment - bytesToPreviousAlignedOffset;
            return unalignedIndexOffset + bytesToNextAlignedOffset;
        }
    }
    /**
     * Read edge indices, which are not zigzag encoded like the triangle indices
     *
     * @param data
     * @param offset
     * @param areIndices32bit
     * @private
     */
    readEdgeIndexBuffer(data, offset, areIndices32bit) {
        let indexFetcher;
        if (areIndices32bit) {
            indexFetcher = (i) => data.getUint32(offset + 4 + 4 * i, true);
        }
        else {
            indexFetcher = (i) => data.getUint16(offset + 4 + 2 * i, true);
        }
        const vertexCount = data.getUint32(offset, true);
        return Array.from({ length: vertexCount }, (_, k) => {
            return indexFetcher(k);
        });
    }
    /**
     * Decodes the quantized vertex data for the given tile. The output buffers are ready to be passed directly to
     * Three.js to be used as the position/uv buffers.
     * @param x
     * @param y
     * @param zoomLevel
     * @param minHeight
     * @param maxHeight
     * @param uBuffer
     * @param vBuffer
     * @param heightBuffer
     * @param center
     * @private
     */
    decodeQuantizedVertexData(x, y, zoomLevel, [minHeight, maxHeight], uBuffer, vBuffer, heightBuffer, center) {
        // Pre-calculate some tile information that we will need for decoding the vertex data
        const tileRect = this._tilingScheme.tileXYToRectangle(x, y, zoomLevel);
        const tileWidth = tileRect.width;
        const tileHeight = tileRect.height;
        const vertexCount = Math.min(uBuffer.length, vBuffer.length, heightBuffer.length);
        const positions = Array.from({ length: vertexCount * 3 }, () => 0);
        const uvs = Array.from({ length: vertexCount * 2 }, () => 0);
        const heightRange = maxHeight - minHeight;
        const scratchCartographic = new Cartographic();
        const scratchPos = new Vector3();
        for (let i = 0; i < vertexCount; i += 1) {
            // Load and normalize the 3 position values
            const uNorm = uBuffer[i] / 32767;
            const vNorm = vBuffer[i] / 32767;
            const heightNorm = heightBuffer[i] / 32767;
            // Decode the values as linear interpolations over their valid ranges
            //
            // The data is quantized to 16 bits over the tile's area in geographic coordinates
            const u = tileRect.west + tileWidth * uNorm;
            const v = tileRect.south + tileHeight * vNorm;
            const height = minHeight + heightRange * heightNorm;
            // Calculate the cartesian position of the vertex relative to the tile's declared center
            scratchCartographic.set(u, v, height);
            const pos = this._tilingScheme.ellipsoid
                .cartesianForCartographic(scratchCartographic, scratchPos)
                .sub(center);
            positions[i * 3 + 0] = pos.x;
            positions[i * 3 + 1] = pos.y;
            positions[i * 3 + 2] = pos.z;
            uvs[i * 2 + 0] = uNorm;
            uvs[i * 2 + 1] = vNorm;
        }
        return [positions, uvs];
    }
    /**
     * Generate the skirt geometry
     *
     * @param positions Position buffer
     * @param uvs Texel coordinates
     * @param center Center of the terrain mesh in ECEF
     * @param minHeight Lowest point on the tileset
     * @param triangleIndices Triangle index buffer
     * @param edgeIndices Buffers for each set of edge indices (west, south, east, north)
     */
    calculateSkirtGeometry(positions, uvs, center, westEdgeIndices, southEdgeIndices, eastEdgeIndices, northEdgeIndices) {
        const skirtPositions = [];
        const skirtUVs = [];
        const skirtIndices = [];
        // Sort indices for triangulation
        westEdgeIndices.sort((a, b) => uvs[a * 2 + 1] - uvs[b * 2 + 1]);
        eastEdgeIndices.sort((a, b) => uvs[b * 2 + 1] - uvs[a * 2 + 1]);
        southEdgeIndices.sort((a, b) => uvs[b * 2] - uvs[a * 2]);
        northEdgeIndices.sort((a, b) => uvs[a * 2] - uvs[b * 2]);
        const edgeIndices = [westEdgeIndices, southEdgeIndices, eastEdgeIndices, northEdgeIndices];
        const scratchCartographic = new Cartographic();
        const scratchPos = new Vector3();
        for (const indices of edgeIndices) {
            const offset = skirtPositions.length / 3;
            for (const i of indices) {
                const x = positions[i * 3 + 0];
                const y = positions[i * 3 + 1];
                const z = positions[i * 3 + 2];
                // Extrude the edge vertices down 10000 world units
                scratchPos.set(x, y, z).add(center);
                this._tilingScheme.ellipsoid.cartographicForPosition(scratchPos, scratchCartographic);
                scratchCartographic.height -= 10000;
                const extrude = this._tilingScheme.ellipsoid
                    .cartesianForCartographic(scratchCartographic, scratchPos)
                    .sub(center);
                // Add vertex positions
                skirtPositions.push(x);
                skirtPositions.push(y);
                skirtPositions.push(z);
                skirtPositions.push(extrude.x);
                skirtPositions.push(extrude.y);
                skirtPositions.push(extrude.z);
                // Copy UV position of the original vertex
                skirtUVs.push(uvs[i * 2 + 0]);
                skirtUVs.push(uvs[i * 2 + 1]);
                skirtUVs.push(uvs[i * 2 + 0]);
                skirtUVs.push(uvs[i * 2 + 1]);
            }
            // Generate skirt indices
            for (let j = 0; j < indices.length * 2 - 3; j++) {
                const curr = offset + j;
                const next = offset + (j + 2);
                const upperL = curr;
                const lowerL = curr + 1;
                const upperR = next;
                const lowerR = next + 1;
                skirtIndices.push(lowerL);
                skirtIndices.push(upperL);
                skirtIndices.push(lowerR);
                skirtIndices.push(upperR);
                skirtIndices.push(lowerR);
                skirtIndices.push(upperL);
            }
        }
        const geometry = new BufferGeometry();
        geometry.setAttribute("position", new Float32BufferAttribute(skirtPositions, 3, false));
        geometry.setAttribute("uv", new Float32BufferAttribute(skirtUVs, 2, false));
        geometry.setIndex(skirtIndices);
        return geometry;
    }
}
function zigZagDecode(value) {
    return (value >> 1) ^ -(value & 1);
}
const HEADER_OFFSET = 0;
const VTX_DATA_OFFSET = 88;
